import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
// TODO: fix import from the app, libs shouldn't depend on any app code
import { openDeleteRoomDialog } from '@app/modules/dashboard/components/confirm-delete-room-dialog/confirm-delete-room-dialog.component';
import { RestrictedElementDirective } from '@auth';
import { EntityStatus, sortUtils } from '@core';
import { IncidentSeverityCountComponent } from '@incidents';
import { IncidentSeverity, UserRolesIds } from '@models';
import { Action, Store } from '@ngrx/store';
import { AuthSelectors } from '@ngrx-store';
import { CdkVirtualScrollViewportWrapperComponent, ConfirmationDialogComponent, EntityStatusComponent, NoDataPlaceholderComponent, SearchInputComponent, StatusCountComponent, VirtualTableScrollNormalizerDirective } from '@ui';

import { RoomSpaceModel, Space } from '../../../models';
import { LocationActions } from '../../../ngrx-store';
import { AddExternalLinkModalComponent } from '../../add-external-link-modal';

@Component({
  imports: [
    MatCardTitle,
    RestrictedElementDirective,
    RouterLink,
    MatIcon,
    MatPrefix,
    MatCardHeader,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatCardContent,
    SearchInputComponent,
    MatSort,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    IncidentSeverityCountComponent,
    StatusCountComponent,
    MatTooltip,
    NoDataPlaceholderComponent,
    MatRowDef,
    MatHeaderRowDef,
    EntityStatusComponent,
    MatSortHeader,
    MatHeaderRow,
    MatRow,
    CdkFixedSizeVirtualScroll,
    CdkVirtualScrollViewport,
    VirtualTableScrollNormalizerDirective,
    CdkVirtualScrollViewportWrapperComponent,
  ],
  selector: 'avs-room-list-table',
  styleUrl: './room-list-table.component.scss',
  templateUrl: './room-list-table.component.html',
})
export class RoomListTableComponent {
  tableId = input<string | null>(null);
  locationId = input.required<string>();
  floorId = input.required<string>();
  roomList = input<RoomSpaceModel[]>([]);
  isMobile = input(false);

  protected dataSource: MatTableDataSource<Space> = new MatTableDataSource<Space>([]);
  protected eEntityStatus = EntityStatus;
  protected statusFilter = signal<'open' | 'history'>('open');
  protected search = signal('');
  protected readonly displayedColumns: string[] = ['position', 'room', 'status', 'incidents', 'devices', 'actions'];
  protected readonly eIncidentSeverity = IncidentSeverity;

  protected showActionColumn = computed(() => {
    return this.userRoleId() === UserRolesIds.ReadOnly ? this.roomList().some(room => room.externalReference) : true;
  });

  private sort = viewChild(MatSort);
  private userRoleId: Signal<UserRolesIds | undefined>;
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private router = inject(Router);
  private dialogService = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    this.userRoleId = toSignal(this.store.select(AuthSelectors.userRole));

    this.dataSource.filterPredicate = this.filterPredicate;
    this.dataSource.sortData = sortUtils.naturalSorting;

    effect(() => {
      this.dataSource.data = this.roomList();
      this.filterTable(this.search(), this.statusFilter());
    });

    effect(() => {
      const sortEl = this.sort();

      if (sortEl) {
        this.dataSource.sort = sortEl;
        this.cdr.detectChanges();
      }
    });

    effect(() => {
      this.filterTable(this.search(), this.statusFilter());
    });
  }

  protected updateRoomStatus(room: RoomSpaceModel, title: string, status: EntityStatus) {
    const statusActionText: 'resume' | 'pause' | 'archive' =
      status === EntityStatus.Active ? 'resume' : status === EntityStatus.Paused ? 'pause' : 'archive';

    this.openConfirmationDialog({
      action: LocationActions.updateRoom({
        data: { status },
        locationId: this.locationId(),
        spaceId: room.id,
      }),
      description: `Are you sure you want to ${statusActionText} the ${room.friendlyName}?`,
      title,
    });
  }

  protected async editFloor(room: RoomSpaceModel) {
    if (!this.isMobile) {
      await this.router.navigate(['/dashboard', this.locationId(), this.floorId(), room.id, 'edit']);

      return;
    }
    this.dialogService.open(AddExternalLinkModalComponent, {
      data: {
        link: room.externalReference,
        locationId: this.locationId(),
        spaceId: room.id,
      },
    });
  }

  protected deleteRoom(room: RoomSpaceModel) {
    openDeleteRoomDialog(this.dialog, {
      floorId: this.floorId(),
      locationId: this.locationId(),
      name: room.friendlyName,
      navigateToParent: false,
      roomId: room.id,
    });
  }

  private openConfirmationDialog({ action, title, description }: { action: Action; description: string; title: string; }) {
    ConfirmationDialogComponent.open(this.dialog, {
      description,
      title,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmation => {
        if (confirmation) {
          this.store.dispatch(action);
        }
      });
  }

  private filterPredicate = (record: Space, filter: string) => {
    const { search, statusFilter } = JSON.parse(filter);

    const isArchived = record.status === EntityStatus.Archived;
    const statusMatch = statusFilter === 'history' ? isArchived : !isArchived;

    if (!statusMatch) return false;

    if (!search) return true;

    return record.friendlyName
      .toLowerCase()
      .includes(search.toLowerCase().trim());
  };

  private filterTable(search: string, statusFilter: 'open' | 'history') {
    this.dataSource.filter = JSON.stringify({
      search,
      statusFilter,
    });
  }
}
